import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import * as Icons from "lucide-react";
import { Link } from "react-router-dom";
import { pickLocale } from "@/lib/helpers";
import { useGuestAuth } from "@/context/GuestAuthContext";
import { resolveImageUrl } from "@/components/ImageUploader";
import PasswordModal from "./PasswordModal";

/** Card representing an apartment (locked or unlocked). */
export default function ApartmentCard({ apartment }) {
  const { i18n, t } = useTranslation();
  const { isUnlocked } = useGuestAuth();
  const [modalOpen, setModalOpen] = useState(false);
  const unlocked = isUnlocked(apartment.id);
  const Icon = Icons[apartment.icon] || Icons.Home;

  const inner = (
    <div className="group relative flex items-center gap-4 bg-white rounded-2xl border border-ink-200/40 pl-3 pr-5 py-3.5 shadow-soft transition-all hover:-translate-y-0.5 hover:shadow-elevated active:scale-[0.99]">
      {apartment.cover_image ? (
        <div className="w-14 h-14 shrink-0 rounded-xl overflow-hidden bg-ivory-100">
          <img src={resolveImageUrl(apartment.cover_image)} alt="" className="w-full h-full object-cover" loading="lazy" />
        </div>
      ) : (
        <div className="w-14 h-14 shrink-0 rounded-xl bg-ivory-100 flex items-center justify-center">
          <Icon strokeWidth={1.4} className="w-5 h-5 text-bronze-400" />
        </div>
      )}
      <div className="min-w-0 flex-1">
        <div className="font-editorial italic text-[24px] leading-tight text-ink-900 truncate">
          {pickLocale(apartment.name, i18n.language)}
        </div>
        <div className="text-[10px] mt-1 font-semibold tracking-overline text-ink-300">
          {unlocked ? t("apartment.unlocked_label") : t("apartment.locked_label")}
        </div>
      </div>
      <div className="shrink-0 ml-2">
        {unlocked ? (
          <Icons.LockOpen strokeWidth={1.4} className="w-[18px] h-[18px] text-bronze-400 animate-unlock-spin" />
        ) : (
          <Icons.Lock strokeWidth={1.4} className="w-[18px] h-[18px] text-ink-300" />
        )}
      </div>
    </div>
  );

  if (unlocked) {
    return (
      <Link
        to={`/appartamento/${apartment.slug}`}
        data-testid={`apartment-card-${apartment.slug}`}
        className="block"
      >
        {inner}
      </Link>
    );
  }
  return (
    <>
      <button
        type="button"
        onClick={() => setModalOpen(true)}
        data-testid={`apartment-card-${apartment.slug}`}
        className="w-full text-left"
      >
        {inner}
      </button>
      <PasswordModal
        open={modalOpen}
        onOpenChange={setModalOpen}
        apartment={apartment}
      />
    </>
  );
}
